import { useMemo } from "react";
import { X } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { buildRefId, parseRefId, refLabel } from "@/lib/actuals";

/**
 * Inline allocation picker. Lets the user tag an actual entry to a specific
 * line item (Interior: Room > Work Item, Exterior/Systems: Category > Variant,
 * Soft: Soft cost line).
 *
 * Props:
 *  - category:  "interior" | "exterior" | "systems" | "soft" | ...
 *  - project:   full project (for options)
 *  - refId:     current selection (may be "")
 *  - onChange:  (newRefId: string) => void
 *  - testPrefix optional suffix for data-testid uniqueness
 */
export default function ActualAllocationPicker({
  category,
  project,
  refId,
  onChange,
  testPrefix = "alloc",
}) {
  const current = parseRefId(refId);

  const clear = () => onChange("");

  if (category === "interior") {
    const rooms = project.rooms || [];
    const selectedRoomId = current?.roomId || "";
    const selectedRoom = rooms.find((r) => r.id === selectedRoomId);
    const items = selectedRoom?.work_items || [];
    const selectedItemValue = current?.itemId || (current?.general ? "general" : "");

    return (
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
        <div>
          <div className="fc-label mb-1">Room</div>
          <Select
            value={selectedRoomId || undefined}
            onValueChange={(v) => onChange(buildRefId("room", { roomId: v }))}
          >
            <SelectTrigger className="fc-input h-9 rounded-md text-sm" data-testid={`${testPrefix}-room`}>
              <SelectValue placeholder={rooms.length ? "Pick a room…" : "No rooms yet"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-black/[0.08]">
              {rooms.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name || humaniseRoomType(r.room_type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="fc-label mb-1">Work item</div>
          <Select
            value={selectedItemValue || undefined}
            onValueChange={(v) =>
              onChange(
                buildRefId("room", {
                  roomId: selectedRoomId,
                  itemId: v === "general" ? null : v,
                })
              )
            }
            disabled={!selectedRoomId}
          >
            <SelectTrigger className="fc-input h-9 rounded-md text-sm" data-testid={`${testPrefix}-item`}>
              <SelectValue placeholder={selectedRoomId ? "Pick an item…" : "Pick a room first"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-black/[0.08]">
              {items.map((it) => (
                <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
              ))}
              <SelectItem value="general">General / Not listed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <ClearBtn onClick={clear} show={!!refId} />
      </div>
    );
  }

  if (category === "exterior" || category === "systems") {
    const list = category === "exterior" ? project.exterior || [] : project.systems || [];
    const enabled = list.filter((i) => i.enabled);
    const pool = enabled.length ? enabled : list;
    const selectedKey = current?.categoryKey || "";
    const selectedCat = pool.find((i) => i.category === selectedKey);
    const variants = selectedCat?.variants || [];
    const selectedVariant = current?.variantId || (current?.general ? "general" : "");

    return (
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
        <div>
          <div className="fc-label mb-1">Category</div>
          <Select
            value={selectedKey || undefined}
            onValueChange={(v) => onChange(buildRefId("cat", { categoryKey: v }))}
          >
            <SelectTrigger className="fc-input h-9 rounded-md text-sm" data-testid={`${testPrefix}-cat`}>
              <SelectValue placeholder="Pick a category…" />
            </SelectTrigger>
            <SelectContent className="bg-white border-black/[0.08]">
              {pool.map((c) => (
                <SelectItem key={c.category} value={c.category}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="fc-label mb-1">Option (if any)</div>
          <Select
            value={selectedVariant || undefined}
            onValueChange={(v) =>
              onChange(
                buildRefId("cat", {
                  categoryKey: selectedKey,
                  variantId: v === "general" ? null : v,
                })
              )
            }
            disabled={!selectedKey}
          >
            <SelectTrigger className="fc-input h-9 rounded-md text-sm" data-testid={`${testPrefix}-variant`}>
              <SelectValue placeholder={selectedKey ? (variants.length ? "Pick an option…" : "General") : "Pick a category first"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-black/[0.08]">
              {variants.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
              ))}
              <SelectItem value="general">General / Not listed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <ClearBtn onClick={clear} show={!!refId} />
      </div>
    );
  }

  if (category === "extensions") {
    const exts = project.extensions || [];
    const selectedExtId = current?.extId || "";
    const selectedExt = exts.find((e) => e.id === selectedExtId);
    let scopeValue = "";
    if (current?.scope === "shell") {
      scopeValue = current.itemId ? `shell:${current.itemId}` : "shell";
    } else if (current?.scope === "room") {
      scopeValue = current.itemId
        ? `room:${current.roomId}:item:${current.itemId}`
        : `room:${current.roomId}`;
    } else if (current?.general) {
      scopeValue = "general";
    }

    const onScopeChange = (v) => {
      if (!selectedExtId) return;
      if (v === "general") {
        onChange(buildRefId("ext", { extId: selectedExtId }));
        return;
      }
      if (v === "shell") {
        onChange(buildRefId("ext", { extId: selectedExtId, scope: "shell" }));
        return;
      }
      if (v.startsWith("shell:")) {
        onChange(buildRefId("ext", { extId: selectedExtId, scope: "shell", itemId: v.slice(6) }));
        return;
      }
      if (v.startsWith("room:")) {
        const rest = v.slice(5);
        const [roomId, , itemId] = rest.split(":");
        onChange(
          buildRefId("ext", {
            extId: selectedExtId,
            scope: "room",
            roomId,
            itemId: itemId || null,
          })
        );
      }
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_auto] gap-2">
        <div>
          <div className="fc-label mb-1">Extension</div>
          <Select
            value={selectedExtId || undefined}
            onValueChange={(v) => onChange(buildRefId("ext", { extId: v }))}
          >
            <SelectTrigger className="fc-input h-9 rounded-md text-sm" data-testid={`${testPrefix}-ext`}>
              <SelectValue placeholder={exts.length ? "Pick an extension…" : "No extensions yet"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-black/[0.08]">
              {exts.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name || humaniseRoomType(e.type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="fc-label mb-1">Shell / room item</div>
          <Select
            value={scopeValue || undefined}
            onValueChange={onScopeChange}
            disabled={!selectedExtId}
          >
            <SelectTrigger className="fc-input h-9 rounded-md text-sm" data-testid={`${testPrefix}-ext-scope`}>
              <SelectValue placeholder={selectedExtId ? "Pick where this spend went…" : "Pick an extension first"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-black/[0.08]">
              <SelectItem value="general">Whole extension · General</SelectItem>
              {selectedExt && (selectedExt.shell_items || []).length > 0 && (
                <SelectItem value="shell">Shell · General</SelectItem>
              )}
              {selectedExt && (selectedExt.shell_items || []).map((it) => (
                <SelectItem key={`s-${it.id}`} value={`shell:${it.id}`}>
                  Shell · {it.name}
                </SelectItem>
              ))}
              {selectedExt && (selectedExt.fitout_rooms || []).map((r) => (
                <SelectItem key={`r-${r.id}`} value={`room:${r.id}`}>
                  {r.name || humaniseRoomType(r.room_type)} · General
                </SelectItem>
              ))}
              {selectedExt && (selectedExt.fitout_rooms || []).flatMap((r) =>
                (r.work_items || []).map((it) => (
                  <SelectItem key={`r-${r.id}-i-${it.id}`} value={`room:${r.id}:item:${it.id}`}>
                    {r.name || humaniseRoomType(r.room_type)} · {it.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <ClearBtn onClick={clear} show={!!refId} />
      </div>
    );
  }

  if (category === "soft") {
    // Soft costs use { id, key, label, enabled, ... } — see backend SoftCostItem.
    const list = (project.soft_costs || []).filter((s) => s.enabled !== false);
    const selected = current?.softId || "";
    return (
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
        <div>
          <div className="fc-label mb-1">Soft cost line</div>
          <Select
            value={selected || undefined}
            onValueChange={(v) => onChange(buildRefId("soft", { softId: v }))}
          >
            <SelectTrigger className="fc-input h-9 rounded-md text-sm" data-testid={`${testPrefix}-soft`}>
              <SelectValue placeholder={list.length ? "Pick a soft cost line…" : "No active soft cost lines"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-black/[0.08]">
              {list.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label || humaniseRoomType(s.key || "Soft cost")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ClearBtn onClick={clear} show={!!refId} />
      </div>
    );
  }

  return null;
}

function ClearBtn({ onClick, show }) {
  if (!show) return <div />;
  return (
    <button
      type="button"
      onClick={onClick}
      className="self-end text-[11px] text-[#78716C] hover:text-[#DC2626] border border-transparent hover:border-black/[0.06] rounded-md h-9 px-2 inline-flex items-center gap-1 transition-colors"
      title="Clear allocation"
      data-testid="alloc-clear"
    >
      <X className="w-3 h-3" /> Clear
    </button>
  );
}

function humaniseRoomType(t = "") {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Re-export so consumers can build labels
export { refLabel };
